<div class="lv-root">
  <header class="lv-top">
    <button type="button" class="lv-icon-btn" (click)="onBack()" aria-label="Back to chat" title="Back to chat">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M15 18l-6-6 6-6" />
      </svg>
    </button>

    <div class="lv-top-copy">
      <div class="lv-top-row">
        <h1>Leave</h1>
        <span class="lv-role" *ngIf="!isLoading" [class.lv-role--admin]="isAdmin">{{ isAdmin ? 'Admin' : 'Member' }}</span>
      </div>
      <p class="lv-subtitle">Balance, requests, and approvals for this tenant.</p>
    </div>

    <button type="button" class="lv-ghost-btn" (click)="reload()" [disabled]="isLoading || isRefreshing" title="Refresh">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" [class.lv-spin]="isRefreshing" aria-hidden="true">
        <path d="M21 12a9 9 0 11-2.6-6.2" />
        <path d="M21 3v6h-6" />
      </svg>
      {{ isRefreshing ? 'Refreshing…' : 'Refresh' }}
    </button>
  </header>

  <div class="lv-alert" *ngIf="error" role="alert">{{ error }}</div>

  <div class="lv-loading" *ngIf="isLoading && !balance">
    <div class="lv-spinner"></div>
    <p>Loading leave…</p>
  </div>

  <ng-container *ngIf="!isLoading || balance">
    <section class="lv-balances" *ngIf="balance" aria-label="Leave balances" [class.lv-balances--quiet]="isRefreshing">
      <article
        *ngFor="let card of balanceCards; trackBy: trackBalanceCard"
        class="lv-balance"
        [ngClass]="'lv-balance--' + card.tone"
        [class.lv-balance--selected]="form.leaveType === card.key"
        (click)="selectLeaveType(card.key); setTab('request')"
        role="button"
        tabindex="0"
        (keydown.enter)="selectLeaveType(card.key); setTab('request')">
        <div class="lv-balance-head">
          <div>
            <p class="lv-balance-label">{{ card.label }}</p>
            <p class="lv-balance-hint">{{ card.hint }}</p>
          </div>
          <div class="lv-ring" [style.--pct]="remainingPercent(card.data)">
            <strong>{{ card.data.remaining }}</strong>
          </div>
        </div>
        <div class="lv-meter" aria-hidden="true">
          <span class="lv-meter-fill" [style.width.%]="remainingPercent(card.data)"></span>
        </div>
        <div class="lv-balance-foot">
          <span><b>{{ card.data.used }}</b> used</span>
          <span><b>{{ card.data.pending }}</b> pending</span>
          <span>of <b>{{ card.data.total }}</b></span>
        </div>
      </article>
    </section>

    <nav class="lv-tabs" aria-label="Leave sections">
      <button type="button" class="lv-tab" [class.lv-tab--active]="activeTab === 'request'" (click)="setTab('request')">Request</button>
      <button type="button" class="lv-tab" [class.lv-tab--active]="activeTab === 'mine'" (click)="setTab('mine')">
        My requests
        <span class="lv-tab-count" *ngIf="pendingMineCount">{{ pendingMineCount }}</span>
      </button>
      <button type="button" class="lv-tab" *ngIf="isAdmin" [class.lv-tab--active]="activeTab === 'team'" (click)="setTab('team')">
        Approvals
        <span class="lv-tab-count lv-tab-count--warn" *ngIf="pendingTeamCount">{{ pendingTeamCount }}</span>
      </button>
    </nav>

    <!-- REQUEST -->
    <section class="lv-panel" *ngIf="activeTab === 'request'">
      <div class="lv-panel-head">
        <div>
          <h2>New request</h2>
          <p>Complete the fields below. Pending requests reserve balance until decided.</p>
        </div>
        <div class="lv-pill-stat" *ngIf="estimatedDays > 0">
          <span>Duration</span>
          <strong>{{ estimatedDays }}d</strong>
        </div>
      </div>

      <div class="lv-section-label">Leave type</div>
      <div class="lv-type-row" role="radiogroup" aria-label="Leave type">
        <button
          type="button"
          *ngFor="let t of leaveTypes"
          class="lv-type-pill"
          [ngClass]="['lv-type-pill--' + t.toLowerCase(), form.leaveType === t ? 'lv-type-pill--active' : '']"
          (click)="selectLeaveType(t)"
          role="radio"
          [attr.aria-checked]="form.leaveType === t">
          <span class="lv-type-pill-title">{{ t }}</span>
          <span class="lv-type-pill-meta" *ngIf="selectedBalance && form.leaveType === t">{{ selectedBalance.remaining }} remaining</span>
        </button>
      </div>

      <div class="lv-section-label">Dates</div>
      <div class="lv-form-grid">
        <label class="lv-field">
          <span>Start date</span>
          <input type="date" [(ngModel)]="form.startDate" (ngModelChange)="onStartDateChange()" />
        </label>
        <label class="lv-field">
          <span>End date</span>
          <input type="date" [(ngModel)]="form.endDate" [disabled]="form.isHalfDay" />
        </label>
      </div>

      <div class="lv-halfday">
        <label class="lv-switch">
          <input type="checkbox" [(ngModel)]="form.isHalfDay" (ngModelChange)="onHalfDayChange()" />
          <span class="lv-switch-ui" aria-hidden="true"></span>
          <span class="lv-switch-copy">
            <strong>Half day</strong>
            <small>0.5 day against balance</small>
          </span>
        </label>
        <div class="lv-segment" *ngIf="form.isHalfDay" role="radiogroup" aria-label="Half day period">
          <button type="button" class="lv-segment-btn" [class.lv-segment-btn--active]="form.halfDayPeriod === 'Morning'" (click)="selectHalfDayPeriod('Morning')">Morning</button>
          <button type="button" class="lv-segment-btn" [class.lv-segment-btn--active]="form.halfDayPeriod === 'Afternoon'" (click)="selectHalfDayPeriod('Afternoon')">Afternoon</button>
        </div>
      </div>

      <div class="lv-section-label">Details</div>
      <label class="lv-field">
        <span>Reason</span>
        <textarea rows="3" [(ngModel)]="form.reason" maxlength="2000" placeholder="Brief note for the approver"></textarea>
        <em class="lv-field-hint">{{ form.reason.length || 0 }} / 2000</em>
      </label>

      <div class="lv-section-label">Attachment</div>
      <div class="lv-attach-box" [class.lv-attach-box--filled]="!!attachmentName">
        <label class="lv-attach-drop">
          <input #attachmentInput type="file" (change)="onFileSelected($event)" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" />
          <div class="lv-attach-ico" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.44 11.05l-8.49 8.49a5 5 0 01-7.07-7.07l9.19-9.19a3.5 3.5 0 014.95 4.95l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
            </svg>
          </div>
          <div class="lv-attach-copy" *ngIf="!attachmentName">
            <strong>Upload supporting file</strong>
            <small>PDF, image, or Office doc · max 5 MB</small>
          </div>
          <div class="lv-attach-copy" *ngIf="attachmentName">
            <strong>{{ attachmentName }}</strong>
            <small>{{ attachmentSizeLabel }} selected</small>
          </div>
        </label>

        <div class="lv-attach-preview" *ngIf="localPreviewIsImage && localPreviewUrl">
          <img [src]="localPreviewUrl" [alt]="attachmentName || 'Attachment preview'" />
        </div>

        <div class="lv-attach-actions" *ngIf="attachmentName">
          <button type="button" class="lv-ghost-btn lv-ghost-btn--sm" (click)="previewLocalAttachment()">View</button>
          <button type="button" class="lv-text-btn" (click)="clearAttachment()">Remove</button>
        </div>
      </div>

      <footer class="lv-form-foot">
        <p class="lv-form-note" *ngIf="selectedBalance as sel">
          {{ sel.remaining }} {{ form.leaveType.toLowerCase() }} day{{ sel.remaining === 1 ? '' : 's' }} available
          <ng-container *ngIf="estimatedDays"> · this request: {{ estimatedDays }}</ng-container>
        </p>
        <button type="button" class="lv-primary-btn" (click)="submitApply()" [disabled]="isSubmitting">
          {{ isSubmitting ? 'Submitting…' : 'Submit request' }}
        </button>
      </footer>
    </section>

    <!-- MY REQUESTS -->
    <section class="lv-panel" *ngIf="activeTab === 'mine'">
      <div class="lv-panel-head">
        <div>
          <h2>My requests</h2>
          <p>Status history for leave you’ve submitted.</p>
        </div>
      </div>

      <div class="lv-empty" *ngIf="myRequests.length === 0">
        <h3>No requests yet</h3>
        <p>Submitted leave will appear here with status and attachments.</p>
        <button type="button" class="lv-primary-btn" (click)="setTab('request')">Create request</button>
      </div>

      <div class="lv-list" *ngIf="myRequests.length > 0">
        <article class="lv-card" *ngFor="let req of myRequests; trackBy: trackRequest">
          <div class="lv-card-main">
            <div class="lv-card-topline">
              <span [class]="typeTone(req.leaveType)">{{ req.leaveType }}</span>
              <span [class]="statusClass(req.status)">{{ req.status }}</span>
            </div>
            <h3>{{ formatDateRange(req) }}</h3>
            <p class="lv-card-meta">
              {{ req.dayCount }} day{{ req.dayCount === 1 ? '' : 's' }}
              <span *ngIf="req.designation"> · {{ req.designation }}</span>
            </p>
            <p class="lv-card-reason">{{ req.reason }}</p>

            <div class="lv-card-attach" *ngIf="req.hasAttachment">
              <button
                type="button"
                class="lv-file-chip"
                (click)="openRequestAttachment(req)"
                [disabled]="openingAttachmentId === req.id"
                title="Open attachment">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M21.44 11.05l-8.49 8.49a5 5 0 01-7.07-7.07l9.19-9.19a3.5 3.5 0 014.95 4.95l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                </svg>
                <span>{{ openingAttachmentId === req.id ? 'Opening…' : (req.attachmentFileName || 'Attachment') }}</span>
                <em>View</em>
              </button>
            </div>

            <p class="lv-card-reject" *ngIf="req.rejectionReason">Rejected: {{ req.rejectionReason }}</p>

            <!-- What the leave agent did with this request -->
            <div class="lv-agent lv-agent--note" *ngIf="req.agentReview as rv"
                 [class.lv-agent--approve]="rv.status === 'approved_by_agent'"
                 [class.lv-agent--review]="rv.status === 'human_review'">
              <div class="lv-agent-head">
                <span class="lv-agent-badge" aria-hidden="true">AI</span>
                <span class="lv-agent-title" [ngSwitch]="rv.status">
                  <ng-container *ngSwitchCase="'reviewing'"><span class="lv-agent-dot"></span>The agent is checking your request…</ng-container>
                  <ng-container *ngSwitchCase="'human_review'"><strong>Under human review</strong><span class="lv-agent-sub"> · not approved or rejected yet</span></ng-container>
                  <ng-container *ngSwitchCase="'approved_by_agent'"><strong>Approved automatically by the agent</strong></ng-container>
                </span>
              </div>
              <p class="lv-agent-reason" *ngIf="rv.status === 'human_review' && rv.reason">{{ rv.reason }}</p>
            </div>
          </div>
          <div class="lv-card-side">
            <button
              type="button"
              class="lv-ghost-btn lv-ghost-btn--sm"
              *ngIf="req.status === 'Pending'"
              (click)="cancelRequest(req)"
              [disabled]="actionRequestId === req.id">
              {{ actionRequestId === req.id ? 'Cancelling…' : 'Cancel' }}
            </button>
            <span class="lv-card-stamp" *ngIf="req.status !== 'Pending'">
              {{ req.reviewedAt ? formatDate(req.reviewedAt) : formatDate(req.createdAt) }}
            </span>
          </div>
        </article>
      </div>
    </section>

    <!-- TEAM -->
    <section class="lv-panel" *ngIf="isAdmin && activeTab === 'team'">
      <div class="lv-panel-head">
        <div>
          <h2>Team approvals</h2>
          <p>Review pending leave for this tenant. You cannot approve your own request.</p>
        </div>
        <div class="lv-pill-stat" *ngIf="pendingTeamCount">
          <span>Needs you</span>
          <strong>{{ pendingTeamCount }}</strong>
        </div>
      </div>

      <div class="lv-empty" *ngIf="teamRequests.length === 0">
        <h3>No team requests</h3>
        <p>When employees apply for leave, they will land here for review.</p>
      </div>

      <div class="lv-list" *ngIf="teamRequests.length > 0">
        <article class="lv-card lv-card--team" *ngFor="let req of teamRequests; trackBy: trackRequest" [class.lv-card--actionable]="canReview(req)">
          <div class="lv-avatar" aria-hidden="true">{{ initials(req.requesterName) }}</div>
          <div class="lv-card-main">
            <div class="lv-card-topline">
              <span [class]="typeTone(req.leaveType)">{{ req.leaveType }}</span>
              <span [class]="statusClass(req.status)">{{ req.status }}</span>
            </div>
            <h3>{{ req.requesterName }}</h3>
            <p class="lv-card-meta">
              {{ req.requesterEmail }}
              <span *ngIf="req.designation"> · {{ req.designation }}</span>
            </p>
            <p class="lv-card-dates">{{ formatDateRange(req) }} · {{ req.dayCount }}d</p>
            <p class="lv-card-reason">{{ req.reason }}</p>

            <div class="lv-card-attach" *ngIf="req.hasAttachment">
              <button
                type="button"
                class="lv-file-chip"
                (click)="openRequestAttachment(req)"
                [disabled]="openingAttachmentId === req.id"
                title="Open attachment">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M21.44 11.05l-8.49 8.49a5 5 0 01-7.07-7.07l9.19-9.19a3.5 3.5 0 014.95 4.95l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                </svg>
                <span>{{ openingAttachmentId === req.id ? 'Opening…' : (req.attachmentFileName || 'Attachment') }}</span>
                <em>View</em>
              </button>
            </div>

            <p class="lv-card-reject" *ngIf="req.rejectionReason">{{ req.rejectionReason }}</p>
            <p class="lv-own" *ngIf="isOwnPending(req)">Awaiting another admin</p>

            <!-- The DPE leave agent's proposal for this pending request -->
            <div class="lv-agent" *ngIf="suggestionOf(req) as sug" [class.lv-agent--approve]="suggests(req, 'approve') || sug.status === 'executed'" [class.lv-agent--reject]="suggests(req, 'reject')">
              <div class="lv-agent-head">
                <span class="lv-agent-badge" aria-hidden="true">AI</span>
                <span class="lv-agent-title" [ngSwitch]="sug.status">
                  <ng-container *ngSwitchCase="'reviewing'"><span class="lv-agent-dot"></span>Agent is reviewing…</ng-container>
                  <ng-container *ngSwitchCase="'ready'">Agent suggests: <strong>{{ sug.decision === 'approve' ? 'Approve' : 'Reject' }}</strong><span class="lv-agent-sub" *ngIf="sug.decision === 'reject'"> · flagged for your review</span></ng-container>
                  <ng-container *ngSwitchCase="'executed'"><strong>Approved automatically by the agent</strong><span class="lv-agent-sub"> · no policy conflicted</span></ng-container>
                  <ng-container *ngSwitchCase="'needs_human'">Agent flagged this for you to decide</ng-container>
                  <ng-container *ngSwitchDefault>Agent review unavailable</ng-container>
                </span>
                <button type="button" class="lv-agent-toggle" *ngIf="sug.status === 'ready' || sug.status === 'needs_human' || sug.status === 'executed'" (click)="toggleSuggestion(req)" [attr.aria-expanded]="openSuggestionIds.has(req.id)">
                  {{ openSuggestionIds.has(req.id) ? 'Hide why' : 'Why?' }}
                </button>
              </div>
              <div class="lv-agent-body" *ngIf="openSuggestionIds.has(req.id)">
                <p class="lv-agent-reason" *ngIf="sug.reason">{{ sug.reason }}</p>
                <div class="lv-agent-policies" *ngIf="sug.policies.length">
                  <strong>Policies it looked at</strong>
                  <div class="lv-agent-policy" *ngFor="let p of sug.policies">
                    <span class="lv-agent-policy-name" *ngIf="p.name">{{ p.name }}</span>
                    <p>{{ p.statement }}</p>
                    <em *ngIf="p.documentName">{{ p.documentName }}</em>
                  </div>
                </div>
                <p class="lv-agent-note" *ngIf="sug.status !== 'executed'">A suggestion only. Nothing happens until you approve or reject.</p>
                <p class="lv-agent-note" *ngIf="sug.status === 'executed'">The agent approves on its own only when none of the policies conflict with a request. Anything else comes to you.</p>
              </div>
            </div>
          </div>
          <div class="lv-card-side lv-card-side--stack" *ngIf="canReview(req)">
            <button type="button" class="lv-primary-btn lv-primary-btn--sm" [class.lv-suggested]="suggests(req, 'approve')" (click)="approveRequest(req)" [disabled]="actionRequestId === req.id">Approve</button>
            <button type="button" class="lv-danger-btn" [class.lv-suggested]="suggests(req, 'reject')" (click)="openReject(req)" [disabled]="actionRequestId === req.id">Reject</button>
          </div>
        </article>
      </div>
    </section>
  </ng-container>
</div>

<!-- Reject modal -->
<div class="lv-modal-backdrop" *ngIf="rejectTarget" (click)="closeReject()">
  <div class="lv-modal" (click)="$event.stopPropagation()" role="dialog" aria-modal="true" aria-labelledby="lv-reject-title">
    <header class="lv-modal-head">
      <h3 id="lv-reject-title">Reject leave</h3>
      <button type="button" class="lv-icon-btn" (click)="closeReject()" aria-label="Close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </header>
    <p class="lv-modal-copy">
      Rejecting <strong>{{ rejectTarget.requesterName }}</strong> · {{ rejectTarget.leaveType }} · {{ formatDateRange(rejectTarget) }}.
      A reason is required.
    </p>
    <label class="lv-field">
      <span>Rejection reason</span>
      <textarea rows="4" [(ngModel)]="rejectReason" placeholder="Explain the decision"></textarea>
    </label>
    <footer class="lv-modal-foot">
      <button type="button" class="lv-ghost-btn" (click)="closeReject()">Keep pending</button>
      <button type="button" class="lv-danger-btn" (click)="confirmReject()" [disabled]="actionRequestId === rejectTarget.id">
        {{ actionRequestId === rejectTarget.id ? 'Rejecting…' : 'Reject request' }}
      </button>
    </footer>
  </div>
</div>

<!-- Attachment viewer -->
<div class="lv-modal-backdrop" *ngIf="attachmentViewer" (click)="closeAttachmentViewer()">
  <div class="lv-modal lv-modal--viewer" (click)="$event.stopPropagation()" role="dialog" aria-modal="true" aria-label="Attachment preview">
    <header class="lv-modal-head">
      <div class="lv-viewer-title">
        <h3>{{ attachmentViewer.name }}</h3>
        <p>{{ attachmentViewer.kind === 'image' ? 'Image preview' : attachmentViewer.kind === 'pdf' ? 'PDF preview' : 'File' }}</p>
      </div>
      <div class="lv-viewer-actions">
        <button type="button" class="lv-ghost-btn lv-ghost-btn--sm" (click)="downloadViewerAttachment()">Download</button>
        <button type="button" class="lv-icon-btn" (click)="closeAttachmentViewer()" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
    </header>
    <div class="lv-viewer-body">
      <img *ngIf="attachmentViewer.kind === 'image'" [src]="attachmentViewer.url" [alt]="attachmentViewer.name" />
      <iframe *ngIf="attachmentViewer.kind === 'pdf'" [src]="attachmentViewer.safeUrl" title="PDF attachment"></iframe>
    </div>
  </div>
</div>
